/**
 * Vista /batch-edits — tutti i batch della Batch Edit API con statistiche aggregate e live polling.
 *
 * KPI in testa (batch, in corso, esiti, record gestiti per tipo), poi l'elenco filtrabile per
 * stato e id: per ogni batch righe caricate, piano (nuovi/aggiornati/cancellati), esiti di
 * esecuzione e avanzamento. Click su una riga → dettaglio /batch-edits/:id.
 * I batch «In attesa di conferma» (soglia di hold superata) hanno un KPI e un filtro dedicati.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBatches } from './hooks.js';
import {
  ACTIVE_STATUSES,
  FINISHED_STATUSES,
  type BatchStatus,
  type BatchSummary,
} from './types.js';
import {
  BATCH_STATUS_LABEL,
  BatchStatusBadge,
  KindTriplet,
  ProgressBar,
  fmtDateTime,
  fmtDuration,
  fmtNum,
  holdShort,
  kindTotals,
} from './ui.js';
import styles from './BatchEdits.module.css';

type StatusFilter = 'all' | 'active' | 'issues' | BatchStatus;

function matchesFilter(b: BatchSummary, f: StatusFilter): boolean {
  if (f === 'all') return true;
  if (f === 'active') return ACTIVE_STATUSES.includes(b.status);
  if (f === 'issues') return ['invalid', 'failed', 'completed-with-errors'].includes(b.status);
  return b.status === f;
}

function Kpi({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}) {
  return (
    <div className={`${styles.kpi} ${accent ?? ''}`}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiValue}>{value}</span>
      {hint && <span className={styles.kpiHint}>{hint}</span>}
    </div>
  );
}

function Totals({ batches }: { batches: BatchSummary[] }) {
  const t = useMemo(() => {
    const acc = {
      onHold: 0,
      active: 0,
      ok: 0,
      issues: 0,
      records: 0,
      new: 0,
      update: 0,
      delete: 0,
      failed: 0,
    };
    for (const b of batches) {
      if (ACTIVE_STATUSES.includes(b.status)) acc.active++;
      if (b.status === 'on-hold') acc.onHold++;
      if (b.status === 'completed') acc.ok++;
      if (['invalid', 'failed', 'completed-with-errors'].includes(b.status)) acc.issues++;
      if (FINISHED_STATUSES.includes(b.status) && b.stats) {
        const k = kindTotals(b);
        acc.records += b.stats.total;
        acc.new += k.new;
        acc.update += k.update;
        acc.delete += k.delete;
        acc.failed += b.stats.byStatus.failed + b.stats.byStatus['not-executed'];
      }
    }
    return acc;
  }, [batches]);

  return (
    <div className={styles.kpiGrid} data-testid="batch-kpis">
      <Kpi label="Batch" value={fmtNum(batches.length)} />
      <Kpi
        label="In corso"
        value={fmtNum(t.active)}
        hint="in esecuzione o in coda"
        accent={styles.kpiAccentRun}
      />
      <Kpi
        label="In attesa di conferma"
        value={fmtNum(t.onHold)}
        hint="soglia di modifiche superata"
        accent={t.onHold ? styles.kpiAccentErr : undefined}
      />
      <Kpi label="Completati" value={fmtNum(t.ok)} accent={styles.kpiAccentOk} />
      <Kpi
        label="Con problemi"
        value={fmtNum(t.issues)}
        hint="non validi, falliti, con errori"
        accent={t.issues ? styles.kpiAccentErr : undefined}
      />
      <Kpi label="Record gestiti" value={fmtNum(t.records)} hint="nei batch eseguiti" />
      <Kpi label="Creazioni" value={fmtNum(t.new)} />
      <Kpi label="Aggiornamenti" value={fmtNum(t.update)} />
      <Kpi label="Cancellazioni" value={fmtNum(t.delete)} />
      <Kpi
        label="Record falliti"
        value={fmtNum(t.failed)}
        accent={t.failed ? styles.kpiAccentErr : undefined}
      />
    </div>
  );
}

/** Esiti di esecuzione (solo per batch eseguiti). */
function Outcome({ b }: { b: BatchSummary }) {
  if (!b.stats || !(FINISHED_STATUSES.includes(b.status) || b.status === 'running')) {
    return <span className={styles.muted}>–</span>;
  }
  const s = b.stats.byStatus;
  const failed = s.failed + s['not-executed'];
  return (
    <span className={styles.countTriplet}>
      <span className={styles.cNew} title="Eseguiti">
        ✓ {fmtNum(s.done)}
      </span>
      <span className={failed ? styles.cDel : styles.cDim} title="Falliti / non eseguiti">
        ✗ {fmtNum(failed)}
      </span>
      {s['pending-dt'] > 0 && (
        <span
          className={styles.cUpd}
          title="Scritti, sincronizzazione Dependency-Track da ritentare"
        >
          DT {fmtNum(s['pending-dt'])}
        </span>
      )}
    </span>
  );
}

export function BatchEditsPage() {
  const { data, isLoading, isError } = useBatches();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  const batches = data ?? [];
  const visible = batches.filter(
    (b) =>
      matchesFilter(b, filter) && b.batchId.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const anyActive = batches.some((b) => ACTIVE_STATUSES.includes(b.status));

  return (
    <div>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Batch Edits</h1>
        <p className={styles.pageSubtitle}>
          Modifiche massive al catalogo inviate via Batch Edit API — aggiornamento in tempo reale.
        </p>
        {anyActive && <span className={styles.liveTag}>LIVE</span>}
      </div>

      {isLoading && <p className={styles.stateMessage}>Caricamento batch…</p>}
      {isError && <p className={styles.errorMessage}>Errore nel caricamento dei batch.</p>}

      {data && (
        <>
          <Totals batches={batches} />

          <div className={styles.toolbar}>
            <input
              className={styles.input}
              type="search"
              placeholder="Cerca per id batch…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Cerca batch"
            />
            <select
              className={styles.select}
              value={filter}
              onChange={(e) => setFilter(e.target.value as StatusFilter)}
              aria-label="Filtra per stato"
              data-testid="batch-status-filter"
            >
              <option value="all">Tutti gli stati</option>
              <option value="active">In corso (esecuzione / coda)</option>
              <option value="issues">Con problemi</option>
              {(Object.keys(BATCH_STATUS_LABEL) as BatchStatus[]).map((s) => (
                <option key={s} value={s}>
                  {BATCH_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            <span className={styles.muted}>
              {visible.length} di {batches.length}
            </span>
          </div>

          {batches.length === 0 ? (
            <p className={styles.stateMessage} data-testid="batch-empty">
              Nessun batch ancora. I batch compaiono qui appena un client chiama{' '}
              <code className={styles.mono}>POST /batch-edit/create-batch/&lt;id&gt;</code>.
            </p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table} data-testid="batch-table">
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>Stato</th>
                    <th>Creato</th>
                    <th className={styles.num}>Chunk</th>
                    <th className={styles.num}>Righe caricate</th>
                    <th>Nuovi / Agg. / Canc.</th>
                    <th>Esiti</th>
                    <th>Avanzamento</th>
                    <th className={styles.num}>Durata</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((b) => (
                    <tr
                      key={b.batchId}
                      className={styles.rowClickable}
                      onClick={() => navigate(`/batch-edits/${encodeURIComponent(b.batchId)}`)}
                      data-testid={`batch-row-${b.batchId}`}
                    >
                      <td>
                        <Link
                          to={`/batch-edits/${encodeURIComponent(b.batchId)}`}
                          className={styles.batchLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {b.batchId}
                        </Link>
                        {b.description && <div className={styles.muted}>{b.description}</div>}
                      </td>
                      <td>
                        <BatchStatusBadge status={b.status} />
                        {b.status === 'queued' && b.queuePosition && (
                          <div className={styles.muted}>posizione {b.queuePosition}</div>
                        )}
                        {b.status === 'on-hold' && b.hold && (
                          <div className={styles.muted} data-testid={`batch-hold-${b.batchId}`}>
                            {holdShort(b.hold)}
                          </div>
                        )}
                      </td>
                      <td className={styles.nowrap}>
                        {fmtDateTime(b.createdAt)}
                        <div className={styles.muted}>{b.createdBy}</div>
                      </td>
                      <td className={styles.num}>
                        {b.chunks.uploaded}
                        {b.chunks.expected !== undefined && `/${b.chunks.expected}`}
                      </td>
                      <td className={styles.num} title="prodotti / clienti / forniture">
                        {fmtNum(b.counts.products + b.counts.customers + b.counts.deployments)}
                      </td>
                      <td>
                        {b.stats || b.planSummary ? (
                          <KindTriplet totals={kindTotals(b)} />
                        ) : (
                          <span className={styles.muted}>da validare</span>
                        )}
                      </td>
                      <td>
                        <Outcome b={b} />
                      </td>
                      <td>
                        <ProgressBar batch={b} />
                      </td>
                      <td className={styles.num}>{fmtDuration(b.durationMs)}</td>
                    </tr>
                  ))}
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={9} className={styles.stateMessage}>
                        Nessun batch corrisponde ai filtri.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
